import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Editor, Toolbar } from 'ngx-editor';
import { Field, ItemsSection } from 'src/app/shared/models/data/documentTemplate';
import { EnquiryHeaderItem } from 'src/app/shared/models/data/enquiry.model';
import { NodeSpec } from 'prosemirror-model';
import { TechnicalDescriptionTemplate } from 'src/app/shared/models/data/technicalDescriptionTemplates.model';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BackendService } from 'src/app/shared/services/backend/backend.service';
import { TeiasoftToasterService } from 'src/app/shared/services/teiasoft-bucket/teiasoft-toaster.service';
import { Globals } from 'src/app/shared/globals/globals';
import { TeiasoftDatetimeComponent } from 'src/app/shared/teiasoft-bucket/teiasoft-datetime/teiasoft-datetime.component';
import { createDocumentEditor, DOCUMENT_TEMPLATE_TOOLBAR } from 'src/app/shared/ngx-editor/document-template-editor';
import { compareItemsByCustomerItemNo } from '../../pdf-utils';


@Component({
  selector: 'app-generic-items-section',
  templateUrl: './generic-items-section.component.html',
  styleUrls: ['./generic-items-section.component.scss']
})
export class GenericItemsSectionComponent implements OnInit, OnChanges, OnDestroy {

  @Input() section!: ItemsSection;
  @Input() theme: string = 'silver';
  @Input() isMobile: boolean = false;
  @Input() items: EnquiryHeaderItem[] = [];
  @Input() uncategorizedItems: EnquiryHeaderItem[] = [];
  @Input() reset!: (sectionName: string) => void;

  // @ViewChild('bulkDatePicker') bulkDatePicker!: ElementRef<HTMLInputElement>;
  @ViewChild(TeiasoftDatetimeComponent) bulkDatePicker!: TeiasoftDatetimeComponent;


  @Output() sectionChange = new EventEmitter<ItemsSection>();
  @Output() addItem = new EventEmitter<EnquiryHeaderItem>();
  @Output() removeItem = new EventEmitter<EnquiryHeaderItem>();

  selectDropdownNode: NodeSpec = {
    group: 'inline',
    inline: true,
    atom: true, // Keep it atomic but ensure it's fully interactive
    attrs: {
      id: { default: '' },
      options: { default: [] },
      value: { default: '' }
    },
    toDOM: (node) => {
      // Generate the select dropdown and allow interaction
      const select = document.createElement('select');

      // Assign the id to the select element
      if (node.attrs['id']) {
        select.id = node.attrs['id'];
      } else {
        select.id = `select_${Math.random().toString(36).substring(2, 9)}`;  // Generate a unique id if not provided
      }

      node.attrs['options'].forEach((option: { value: string; label: string }) => {
        const optionElement = document.createElement('option');
        optionElement.value = option.value;
        optionElement.textContent = option.label;

        // Set the selected attribute based on the value in the node's attrs
        if (node.attrs['value'] === option.value) {
          optionElement.selected = true;
        }

        select.appendChild(optionElement);
      });

      // Ensure select is interactive by not using atom-like behavior for interaction
      select.addEventListener('mousedown', (event) => {
        event.stopPropagation(); // Prevent ProseMirror from blocking the interaction
      });

      select.addEventListener('change', (event) => {
        const target = event.target as HTMLSelectElement;
        const selectedValue = target.value;

        if (select.id) {
          if (!this.section.technicalDescription.selectedValues)
            this.section.technicalDescription.selectedValues = {};
          this.section.technicalDescription.selectedValues![select.id] = selectedValue;
          this.sectionChange.emit(this.section);
        }
      });

      return select;
    },
    parseDOM: [{
      tag: 'select',
      getAttrs: (dom) => {
        const options = Array.from(dom.querySelectorAll('option')).map((option: HTMLOptionElement) => ({
          value: option.value,
          label: option.textContent || option.value
        }));
        const value = (dom as HTMLSelectElement).value;  // Capture the selected value
        const id = (dom as HTMLSelectElement).id;  // Capture the id if present
        return { options, value, id };
      }
    }]
  };

  constructor(public globals: Globals, private translate: TranslateService, private dialog: MatDialog, private backendService: BackendService, private toast: TeiasoftToasterService) { }

  selectedItems: Set<EnquiryHeaderItem> = new Set(); // Track selected items
  gridColumns: string[] = [];
  columnsToShowItem: string[] = ["customerItemNo", "unitSystem", "sms.description", "marketFamily.name", "optionDate", "customerDeliveryDate", "applicableNorms", "remark", "normGrades.normGradeCode", "gradeCustomerAnalysisLimitation.limitation",
    "gradeCustomerOtherLimitation.limitation", "rawMaterialGrade.rawMaterialGradeCode", "npsSchedule.npsDescription", "od", "odUom", "odTolerancePlus", "odToleranceMinus", "odToleranceUom", "id", "idUom", "idTolerancePlus", "idToleranceMinus", "idToleranceUom", "wt", "wtUom", "typeOfTolerance", "tubeExecution.description", "wtTolerancePlus", "wtToleranceMinus", "wtToleranceUom",
    "quantity", "quantityUom", "lengthType", "lengthMin", "lengthMax", "lengthUom", "totalWeight", "totalWeightUom", "mill.shortName", "surfaceType.name", "endsCondition", "sql.name", "offerScheme.offerSchemeDescription", "ut", "hydroTest", "ht", "htAdditional", "inspectionType3Dot2", "multipleCutting",
    "icrIssued", "itrIssued", "idrIssued", "feasibilityStatus", "costsStatus", "deliveryStatus"];
  isItemPickerOpen = false;
  technicalDescriptionEditor: Editor = createDocumentEditor({ nodes: { select_dropdown: this.selectDropdownNode } });
  freeTextEditor: Editor = createDocumentEditor();
  showTechnicalDescriptionModal: boolean = false;
  bulkDeliveryDate?: number;



  @ViewChild('promptTemplateName') promptTemplate!: TemplateRef<any>;

  newTemplateName = '';
  newTemplateIsValid = true;

  toolbar: Toolbar = DOCUMENT_TEMPLATE_TOOLBAR;

  ngOnInit(): void {
    this.gridColumns = this.getVisibleFields().map(field => field.label);

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['section'] && !changes['section'].isFirstChange()) {
      const currentSection = changes['section'].currentValue as ItemsSection;

      if (currentSection) {
        this.applySelectedValues(); // Reapply selected values in case the section changes
      }
    }
  }

  ngOnDestroy(): void {
    this.technicalDescriptionEditor.destroy();
    this.freeTextEditor.destroy();
  }

  onChangeDeliveryDate(date: string, item: EnquiryHeaderItem) {
    item.deliveryDate = Number(date);
  }

  applySelectedValues(): void {
    const sanitizedSectionName = this.getSanitizedSectionName(this.section.sectionName);
    const editorContainer = document.querySelector(`.${sanitizedSectionName}`);

    if (!editorContainer) {
      return;
    }

    const selects = editorContainer.querySelectorAll('select');
    selects.forEach(select => {
      const selectId = select.id;
      const selectedValue = this.section.technicalDescription.selectedValues?.[selectId];
      // If a selected value exists for this select, set it
      if (selectedValue) {
        select.value = selectedValue;
      } else {
        // set the first option as default if no value is found
        select.selectedIndex = 0;
      }
    });
  }


  openItemPicker() {
    this.isItemPickerOpen = true;
  }

  closeItemPicker() {
    this.isItemPickerOpen = false;
  }

  toggleFieldVisibility(field: Field) {
    field.hidden = !field.hidden;
    this.sectionChange.emit(this.section);
    this.gridColumns = this.getVisibleFields().map(f => f.label);
  }

  getNestedValue(obj: any, key: any) {
    return key.split('.').reduce((o: any, k: any) => (o || {})[k], obj);
  }

  getPropertyLabel(label: string) {
    return this.translate.instant('quotation.' + label.charAt(0).toUpperCase() + label.slice(1));

  }

  getVisibleFields(): Field[] {
    return this.section.fields.filter(field => !field.hidden);
  }

  formatWeeks(weeks: number): string {
    return `${weeks} week${weeks > 1 ? 's' : ''}`;
  }

  getFormattedDate(dateMillis?: number): string {
    if (!dateMillis)
      return '';

    const date = new Date(dateMillis);
    if (isNaN(date.getTime())) return '';

    // Get full year and last two digits
    const fullYear = date.getFullYear();
    const yearSuffix = fullYear.toString().slice(-2);

    const firstJan = new Date(fullYear, 0, 1);
    const diffDays = Math.floor((date.getTime() - firstJan.getTime()) / (1000 * 60 * 60 * 24));
    const weekNumber = Math.ceil((diffDays + firstJan.getDay() + 1) / 7);

    return `W${weekNumber}-${yearSuffix}`;
  }

  isFieldHideable(field: Field): boolean {
    return field.hideable;
  }

  onRemoveItem(item: EnquiryHeaderItem) {
    this.removeItem.emit(item);
  }

  onAddItem(item: any) {
    this.addItem.emit(item);
  }

  addSelectedItems() {
    this.selectedItems.forEach(item => {
      this.addItem.emit(item);
    });
    this.selectedItems.clear(); // Clear selection after adding
    this.closeItemPicker();
  }

  toggleSelection(item: EnquiryHeaderItem) {
    if (this.selectedItems.has(item)) {
      this.selectedItems.delete(item);
    } else {
      this.selectedItems.add(item);
    }
  }

  onRowsSelected(selectedRows: EnquiryHeaderItem[]) {
    this.selectedItems = new Set(selectedRows);  // Store selected items from the grid
  }

  getSectionClass(sectionName: string): { [key: string]: boolean } {
    const sanitizedSectionName = this.getSanitizedSectionName(sectionName);
    return {
      'ngx-editor-content': true,
      [sanitizedSectionName]: true
    };
  }

  getSanitizedSectionName(sectionName: string): string {
    return sectionName.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-_]/g, '');
  }



  // Same ordering used by the generated PDF (see pdf-utils.generateGenericItemsSection)
  sortByCustomerItemNo(item1: EnquiryHeaderItem, item2: EnquiryHeaderItem): number {
    return compareItemsByCustomerItemNo(item1, item2);
  }

  onEditorChange(newValue: string) {
    this.section.technicalDescription.value = newValue;
    this.section.technicalDescription.isModified = true;
    this.sectionChange.emit(this.section);
  }

  onCheckboxChange(propertyName: keyof ItemsSection): void {
    this.sectionChange.emit(this.section);
  }

  hasSameUom(uomKey: keyof EnquiryHeaderItem): boolean {
    if (!this.section.items || this.section.items.length === 0) {
      return false;  // No items, so checkbox should be disabled
    }

    const firstUom = this.section.items[0][uomKey];
    return this.section.items.every(item => item[uomKey] === firstUom);
  }

  onLoadTemplate(template: TechnicalDescriptionTemplate) {
    this.section.technicalDescription.value = template.value;
  }

  onInputTemplateName($event: any) {
    this.newTemplateName = $event;
  }

  openNewTemplateDialog(): void {
    const dialogRef: MatDialogRef<any> = this.dialog.open(this.promptTemplate, {
      data: {} // No data is passed here
    });


    dialogRef.afterClosed().subscribe((templateName: string) => {
      if (templateName && templateName.trim() !== '') {
        this.saveTechnicalDescriptionTemplate(templateName.trim());
      }
    });
  }


  onSaveTemplateClick() {
    this.openNewTemplateDialog();
  }

  saveTechnicalDescriptionTemplate(templateName: string) {
    const payload = {
      templateName: templateName,
      value: this.section.technicalDescription.value
    };

    this.backendService.createTechnicalDescriptionTemplate(payload).subscribe({
      next: () => {
        this.toast.show("success", this.translate.instant("commons.success"));
      }
    });
  }

  openDatePicker() {
    if (this.bulkDatePicker) {
      this.bulkDatePicker.openDatePicker();
    }
  }

  applyDeliveryDateToAll(selectedDate: any) {
    if (!selectedDate) return; // Prevent empty value update

    this.section.items.forEach((item: any) => {
      item.deliveryDate = Number(selectedDate);
    });
  }

}
